import { useEffect, useState } from 'react';
import { Clock, Mail, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Visible to any user whose account was terminated within the last 24 hours.
 * Reminds them their work-email login is being revoked, counts down to the
 * cutoff, and nudges them to verify their personal email is on file.
 *
 * Props: `user` (the AuthContext user object). The banner renders nothing
 * outside the 24-hour window.
 */
export default function OffboardingCountdownBanner({ user }) {
  const terminatedAt = user?.terminated_at;
  const isTerminated = !!user?.terminated;

  const cutoff = terminatedAt
    ? new Date(new Date(terminatedAt).getTime() + 24 * 60 * 60 * 1000)
    : null;

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!isTerminated || !cutoff) return;
    const id = setInterval(() => setNow(new Date()), 60 * 1000); // tick every minute
    return () => clearInterval(id);
  }, [isTerminated, cutoff]);

  if (!isTerminated || !cutoff) return null;
  const msLeft = cutoff.getTime() - now.getTime();
  if (msLeft <= 0) return null; // window has passed — hide

  const hoursLeft = Math.floor(msLeft / (60 * 60 * 1000));
  const minutesLeft = Math.floor((msLeft % (60 * 60 * 1000)) / (60 * 1000));
  const hasPersonalEmail = !!user?.personal_email && user.personal_email !== user.email;

  return (
    <div className="rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5 shadow-sm"
         data-testid="offboarding-countdown-banner">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center flex-shrink-0">
          <ShieldAlert className="w-5 h-5 text-amber-700" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-bold text-slate-900 text-lg">Work-email access ends soon</h3>
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-200/70 text-amber-900"
                  data-testid="countdown-pill">
              <Clock className="w-3.5 h-3.5" strokeWidth={2} />
              {hoursLeft > 0 ? `${hoursLeft}h ${minutesLeft}m` : `${minutesLeft}m`} remaining
            </span>
          </div>
          <p className="text-sm text-slate-700 mt-1.5">
            Your access to <span className="font-semibold">{user.email}</span> will be revoked at{' '}
            <span className="font-semibold whitespace-nowrap">
              {cutoff.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
            </span>.
            After that, log in with your personal email to download payslips, letters and reimbursement history.
          </p>

          {hasPersonalEmail ? (
            <div className="mt-3 inline-flex items-center gap-2 text-xs bg-white/70 border border-amber-200 rounded-lg px-3 py-2">
              <Mail className="w-3.5 h-3.5 text-amber-700" strokeWidth={2} />
              <span className="text-slate-700">Personal email on file:</span>
              <span className="font-semibold text-slate-900" data-testid="banner-personal-email">{user.personal_email}</span>
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid="banner-missing-personal-email">
              <span className="inline-flex items-center gap-2 text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded-lg px-3 py-2">
                <Mail className="w-3.5 h-3.5" strokeWidth={2} />
                <span className="font-semibold">No personal email on file</span> — please update before access ends.
              </span>
              <Link to="/profile"
                    className="text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700"
                    data-testid="banner-update-profile-link">
                Update profile →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
